import { Component, createContext } from '~preact'
import PropTypes from "prop-types";
import { ClockV2 } from "@green24/npm-javascript-utils";
import { ValueUpdateListeners } from "../../class/ValueUpdateListeners";

export let LifespanContext = createContext();
/**
 * A public higher-order component to access the imperative API
 */
export const withLifespan = ChildComponent => {
	class HOC extends Component {
		_clock;

		constructor() {
			super();

			this.state = {
				lifespanEnded: false,
				progress: NaN,
			};

			this._valueUpdateListeners = new ValueUpdateListeners(this);
		}

		componentDidMount() {
			const {lifespan, duration} = this.props;

			this._createClock(lifespan ?? duration);

			this._valueUpdateListeners.add(["props.lifespan", "props.duration"], (lifespan, _, duration) => {
				this._createClock(lifespan, duration);
			});
		}

		componentDidUpdate(previousProps, previousState, snapshot) {
			this._valueUpdateListeners.componentDidUpdate(previousProps, previousState);
		}

		componentWillUnmount() {
			this._stopClock();
		}

		render({lifespanTickFrequency, lifespan, duration, propsToPropagate, ...props}, {progress, lifespanEnded}) {
			const contextData = {
				lifespan: lifespan ?? duration,
				lifespanTickFrequency,
				progress,
				lifespanEnded,
				startClock: () => this._clock?.start(),
				pauseClock: () => this._clock?.pause(),
				stopClock: () => this._clock?.stop(),
			};

			return (
				<LifespanContext.Provider value={contextData}>
					<ChildComponent
						{...props}
						{...propsToPropagate}

						lifespanContext={contextData}

						lifespanProgress={progress}
						lifespanEnded={lifespanEnded}

						onLifespanClockStart={contextData.startClock}
						onLifespanClockPause={contextData.pauseClock}
						onLifespanClockStop={contextData.stopClock}
					/>
				</LifespanContext.Provider>
			);
		}

		_createClock(duration) {
			this._stopClock();

			if(duration <= 0 || duration == Infinity) return;

			this._clock = new ClockV2(this.props.lifespanTickFrequency, {
				automaticBreakpointTime: duration,
			});

			this._clock.onTick(() => {
				this.setState({
					progress: this._clock.progress,
				});
			});

			this._clock.onStop(() => {
				this.setState({
					lifespanEnded: true,
				});
			});

			this._clock.start();
		}

		_stopClock() {
			if(!this._clock) return;

			this._clock.onStop(() => null);
			this._clock.stop();
		}

		static get displayName() {
			return "withLifespan(" + (ChildComponent.displayName || ChildComponent.name) + ")";
		}

		static get wrappedComponent() {
			return ChildComponent;
		}

		static get propTypes() {
			return {
				lifespan: PropTypes.number,
				duration: PropTypes.number,

				propsToPropagate: PropTypes.object,

				lifespanTickFrequency: PropTypes.number,
			}
		}

		static get defaultProps() {
			return {
				lifespan: 0,
				duration: 0,
				lifespanTickFrequency: 10,
			}
		}
	}

	return HOC;
};

export default withLifespan;
